import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Row, Col, Button, Modal, message, Spin, Tag } from "antd";
import Card from "../components/Card";
import { NavLink } from "react-router-dom";
import { APIKey, User } from "../helpers/shared-interfaces";
import { request, responseHandler } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import * as Sentry from "@sentry/browser";
import { DELETE_USER, GENERATE_API_KEY, DELETE_API_KEY, GET_USER_WITH_API_KEYS } from "../gql/users";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  id: string | number;
  showDialog: boolean;
  showApiDialog: boolean;
  selectedApiKeyId: string | null;
  user: User;
}

interface RouteParams {
  id: string;
}

function ModalWrapper(
  showDialog: boolean,
  deleteUser: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteUser}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this user?</div>
    </Modal>
  );
}

function ModalAPIWrapper(
  showDialog: boolean,
  deleteApiKey: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteApiKey}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this API key?</div>
    </Modal>
  );
}

export default class ViewUser extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      user: {
        id: "1",
        name: "",
        email: "",
        role: {
          id: "1",
          name: "",
          users: [],
        },
        apiKeys: [],
        department: {
          id: "2",
          name: "",
          users: [],
        },
      },
      isLoading: true,
      id: "",
      name: "",
      hasChanged: false,
      showDialog: false,
      showApiDialog: false,
      selectedApiKeyId: null,
    };
  }

  toggleDeleteConfirmationModal = (): void => {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  };

  redirectToAllUsersPage = (): void => {
    this.props.history.push("/users");
  };

  getUserData = async (): Promise<void> => {
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: GET_USER_WITH_API_KEYS,
        variables: { id },
      });

      if (!data.errors) {
        this.setState({ user: data.data.user });
      } else {
        message.error("Failed to get user data");
      }
    } catch (error) {
      message.error(`Failed to get user data - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  deleteUser = async (): Promise<void> => {
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: DELETE_USER,
        variables: { id },
      });

      if (!data.errors && data.data.deleteUser.result) {
        message.success("User deleted successfuly");
        this.redirectToAllUsersPage();
      } else {
        message.error("Failed to delete user");
      }
    } catch (error) {
      message.error(`Failed to delete user - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showDialog: false });
    }
  };

  deleteApiKey = async (): Promise<void> => {
    const { selectedApiKeyId } = this.state;
    if (!selectedApiKeyId) return;

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_API_KEY,
        variables: { apiKeyId: selectedApiKeyId },
      });

      const { handled } = responseHandler(response, "deleteApiKey");

      if (handled) {
        message.error("Failed to delete API key");
      } else {
        message.success("API key deleted successfully");
        this.setState((state) => ({
          user: {
            ...state.user,
            apiKeys: state.user.apiKeys.filter(
              (key) => key.id !== selectedApiKeyId,
            ),
          },
          selectedApiKeyId: null,
        }));
      }
    } catch (error) {
      message.error(`Failed to delete API key - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ showApiDialog: false, isLoading: false });
    }
  };

  toggleDeleteApiModal = () => {
    const prevState = this.state.showApiDialog;
    this.setState({ showApiDialog: !prevState });
  };

  toggleApiDelete = (apiKeyId: string) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    this.setState({ selectedApiKeyId: apiKeyId });
    this.toggleDeleteApiModal();
  };

  generateApiKey = async (): Promise<void> => {
    interface Response {
      apiKey: APIKey;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: GENERATE_API_KEY,
        variables: {
          userId: this.state.user.id,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "generateApiKey",
      );

      this.setState((state) => ({
        user: {
          ...state.user,
          apiKeys: [...state.user.apiKeys, mutation.apiKey],
        },
      }));

      message.success("API key generated successfully");
    } catch (error) {
      message.error(`Failed to generate API key - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getUserData();
    });
  }

  render(): JSX.Element | null {
    const { isLoading, id, showDialog, user, showApiDialog } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {ModalWrapper(
              showDialog,
              this.deleteUser,
              this.toggleDeleteConfirmationModal,
            )}
            {ModalAPIWrapper(
              showApiDialog,
              this.deleteApiKey,
              this.toggleDeleteApiModal,
            )}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={12}
              >
                <div>User Name</div>
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      marginBottom: "0px",
                    }}
                    data-cy="test-view-user-username"
                  >
                    {user && user.name.toUpperCase()}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                }}
                span={12}
              >
                <EntitledSection entitlement={entitlements.Users.edit}>
                  <NavLink to={`/users/${id}/edit`}>
                    <Button
                      icon={<EditOutlined />}
                      size="large"
                      style={{
                        backgroundColor: "  rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "10px",
                      }}
                      data-cy="test-user-edit-button"
                    >
                      Edit
                    </Button>
                  </NavLink>
                </EntitledSection>
                <EntitledSection entitlement={entitlements.Users.delete}>
                  <Button
                    icon={<DeleteOutlined />}
                    size="large"
                    onClick={this.toggleDeleteConfirmationModal}
                    data-cy="test-user-delete-button"
                  >
                    Delete
                  </Button>
                </EntitledSection>
              </Col>
            </Row>

            <Row>
              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  Email
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: "16px",
                      marginBottom: "0px",
                    }}
                  >
                    {user && user.email}
                  </h1>
                </div>
              </Col>
              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  Department
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: "16px",
                      marginBottom: "0px",
                    }}
                  >
                    <NavLink to={`/departments/${user?.department.id}`}>
                      {user?.department.name}
                    </NavLink>
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  Role
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: "16px",
                      marginBottom: "0px",
                    }}
                  >
                    <NavLink to={`/roles/${user?.role.id}`}>
                      {user?.role.name}
                    </NavLink>
                  </h1>
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: "30px" }}>
              <EntitledSection entitlement={entitlements.Users.create}>
                <Button
                  size="large"
                  onClick={this.generateApiKey}
                  data-cy="test-user-delete-button"
                >
                  Generate Key
                </Button>
                <div
                  style={{
                    fontWeight: "bold",
                    marginTop: "10px",
                  }}
                >
                  {user &&
                    user.apiKeys.map((apiKey, index) => (
                      <Tag
                        key={apiKey.id}
                        closable
                        id={`api-key-tag-${index}`}
                        onClose={this.toggleApiDelete(apiKey.id)}
                        style={{ padding: "10px", marginTop: "5px" }}
                      >
                        {apiKey.key}
                      </Tag>
                    ))}
                </div>
              </EntitledSection>
            </div>
            {/* )} */}
          </div>
        </Card>
      </Spin>
    );
  }
}
